import React, { useState, useCallback } from 'react';
import ApiResponse from '@core/types/apiResponse';
import { ErrorResponse } from '@core/types/errorResponse';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { useErrorModalStore } from '@infrastructure/api/store/errorModalStore';
import { showSuccess } from '@infrastructure/api/utils/toastNotifications';

export type FormFieldErrors<T> = Partial<Record<keyof T, string[]>>;

export interface UseFormModalConfig<T extends Record<string, any>> {
  /** Initial (and post-reset) form values. */
  initial: T;
  /** Synchronous client-side validation. Return field errors + flat messages. */
  validate?: (form: T) => { errors: FormFieldErrors<T>; messages: string[] };
  /** Performs the create/update call. */
  submit: (form: T) => Promise<ApiResponse<any>>;
  /** Called after a successful submit (e.g. close modal + reload). Runs after reset. */
  onSuccess?: () => void;
  /**
   * @deprecated No longer used. API errors are surfaced via `useApiError`, which
   * shows the server's message (field errors, validation, or system error).
   */
  errorFallback?: string;
}

export interface UseFormModalResult<T extends Record<string, any>> {
  form: T;
  setForm: React.Dispatch<React.SetStateAction<T>>;
  formErrors: FormFieldErrors<T>;
  setFormErrors: React.Dispatch<React.SetStateAction<FormFieldErrors<T>>>;
  busy: boolean;
  /** Generic change handler factory for native inputs (text/textarea/select/date). */
  handleChange: (
    field: keyof T
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  /** Sets a single field value programmatically (e.g. checkbox/switch). */
  setFieldValue: (field: keyof T, value: unknown) => void;
  reset: () => void;
  handleSubmit: () => Promise<void>;
}

/**
 * Shared create/edit modal form plumbing: value tracking, field errors, busy
 * state, validation, submit, and reset. Replaces the per-modal copy-paste of
 * `form` / `formErrors` / `handleChange` / `resetForm` / `handleSubmit`.
 * Pair with {@link FormModal} for config-driven rendering.
 */
export function useFormModal<T extends Record<string, any>>(
  config: UseFormModalConfig<T>
): UseFormModalResult<T> {
  const { initial, validate, submit, onSuccess } = config;

  const [form, setForm] = useState<T>(initial);
  const [formErrors, setFormErrors] = useState<FormFieldErrors<T>>({});
  const [busy, setBusy] = useState(false);

  const showError = useApiError();
  const showValidation = useErrorModalStore((s) => s.showValidation);

  const clearFieldError = useCallback((field: keyof T) => {
    setFormErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const handleChange = useCallback(
    (field: keyof T) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, [field]: value }));
        clearFieldError(field);
      },
    [clearFieldError]
  );

  const setFieldValue = useCallback(
    (field: keyof T, value: unknown) => {
      setForm((f) => ({ ...f, [field]: value }));
      clearFieldError(field);
    },
    [clearFieldError]
  );

  const reset = useCallback(() => {
    setForm(initial);
    setFormErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(async () => {
    if (validate) {
      const { errors, messages } = validate(form);
      if (messages.length > 0) {
        setFormErrors(errors);
        showValidation(messages);
        return;
      }
    }

    setBusy(true);
    try {
      const response = await submit(form);
      if (response.isSuccess()) {
        showSuccess(response.getData()?.message);
        reset();
        onSuccess?.();
      } else {
        const error = response.getError() as ErrorResponse;
        const fieldErrors = (error?.errors as FormFieldErrors<T>) ?? {};
        setFormErrors(fieldErrors);
        showError(error);
      }
    } catch (error) {
      showError(error as ErrorResponse);
    } finally {
      setBusy(false);
    }
  }, [validate, submit, onSuccess, form, reset, showError, showValidation]);

  return {
    form,
    setForm,
    formErrors,
    setFormErrors,
    busy,
    handleChange,
    setFieldValue,
    reset,
    handleSubmit,
  };
}
